/**
 * Pop-out window helper — the platform seam for the three queue pop-outs
 * (Task 4093).
 *
 * `AgentSessionModal`, `CustomerHistoryModal` and `ReviewThreadModal` each open
 * a real OS window rather than an in-page modal, and each did it by importing
 * `WebviewWindow` from `@tauri-apps/api/webviewWindow` directly. Two of those
 * three — customer history and review thread — are in-scope support surfaces an
 * operator reaches by clicking, so they cannot simply be dropped from the
 * browser build the way Reports and Skills are.
 *
 * This module isolates the Tauri dependency behind one lazily-imported function
 * so the modals themselves are platform-agnostic and `@tauri-apps` stays out of
 * the web bundle.
 *
 * ## There is deliberately no browser implementation here (Task 4094)
 *
 * 4094 resolved the browser side **without** `window.open`, so this function
 * stays desktop-only and its browser return value is now a genuine
 * "this platform has no OS windows" rather than a placeholder.
 *
 * Ben's decision (2026-08-26), diverging from the task doc's literal wording:
 * Customer History and Review Thread render as **in-page panels** inside the
 * live app in a browser. Two problems disappear as a result:
 *
 *   - **Popup blockers.** `window.open` only survives inside a direct user
 *     gesture, and both call sites open from a store update read in an effect
 *     — one of them behind an `await` on an interaction lookup. It works on a
 *     developer machine with blocking off and fails for the operator.
 *   - **Authentication.** A popped-out document re-runs `main.tsx`, whose
 *     `?ch=1` / `?rt=1` bootstraps call `getEnvironmentConfigs()` — a Tauri
 *     command that throws by design in the browser build. The new document
 *     would load and immediately die. A panel inherits the running app's
 *     configured `api` client and needs no bootstrap at all.
 *
 * Freshness follows: "re-open the same customer and get current data" is a
 * React remount in the panel, not named-target window reuse.
 *
 * `AgentSessionModal` remains genuinely unsupported in a browser (it is an
 * engineering surface, explicitly out of 4094's scope) and still shows the
 * "not available in the browser" toast on the `unsupported` result.
 */

import { IS_TAURI } from './platform'

/** Geometry and chrome for a pop-out window. Mirrors the Tauri options used today. */
export interface PopoutOptions {
  /** Stable window identifier. Also the Tauri window label. */
  label: string
  /** URL relative to the app root, including the `?sv=1` / `?ch=1` / `?rt=1` marker. */
  url: string
  title: string
  width: number
  height: number
  minWidth?: number
  minHeight?: number
}

/** What `openPopout` did, so callers can report accurately rather than assume. */
export type PopoutResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'failed'; error?: unknown }

/**
 * Open a pop-out window, replacing any existing one with the same label.
 *
 * On desktop this is the exact sequence the three modals used inline before:
 * look up the label, close what is there, pause briefly for teardown, then
 * construct the new window. The pause is load-bearing — without it the new
 * window can be created before the old one has released its label.
 */
export async function openPopout(
  options: PopoutOptions,
): Promise<PopoutResult> {
  if (!IS_TAURI) {
    // A browser has no OS windows. The two in-scope support pop-outs no longer
    // reach here — they render in-page panels instead (Task 4094) — so the only
    // remaining caller on this path is `AgentSessionModal`, which is
    // desktop-only by design and reports this to the operator as a toast.
    return { ok: false, reason: 'unsupported' }
  }

  try {
    const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow')

    // Close any existing window with this label so the new window always
    // fetches the latest data.
    const existing = await WebviewWindow.getByLabel(options.label)
    if (existing) {
      try {
        await existing.close()
      } catch {
        /* already gone */
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 50))
    }

    new WebviewWindow(options.label, {
      url: options.url,
      title: options.title,
      width: options.width,
      height: options.height,
      minWidth: options.minWidth,
      minHeight: options.minHeight,
      center: true,
      resizable: true,
      decorations: true,
    })
    return { ok: true }
  } catch (error) {
    return { ok: false, reason: 'failed', error }
  }
}
